import { ExpiredIcon } from '@/assets/icons/expired';
import { ApprovedIcon } from '@/assets/icons/info-icons/approved';
import { RejectedBannerIcon } from '@/assets/icons/info-icons/rejectedBanner';
import { Banner } from '@/components/basicComponents/banner';
import Button from '@/components/basicComponents/button';
import { useResponsive } from '@/hooks/use-responsive';
import { MainWrap, PaddingWrap } from '@/styles/common';
import { themeVar } from '@/styles/theme';
import { useState } from 'react';
import { Helmet as Head } from 'react-helmet';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import useRefresh from '../../hooks/use-refresh';

enum STATUS_ENUM {
  SENT = 'sent',
  EXPIRED = 'expired'
}

const ManagerSignatureExpiredPage = () => {
  useRefresh();
  const navigate = useNavigate();
  const { isMobile } = useResponsive();
  const [searchParams, setSearchParams] = useSearchParams();

  const sentNewEmail = searchParams.get('sentnewemail') || '';

  const [status, setStatus] = useState<STATUS_ENUM>(sentNewEmail === 'true' ? STATUS_ENUM.SENT : STATUS_ENUM.EXPIRED);

  const routeToTransactionsPage = () => {
    navigate('/transactions');
  };

  const getContentByStatus = () => {
    switch (status) {
      case STATUS_ENUM.SENT:
        return (
          <>
            <SubTitle>Manager Signature</SubTitle>
            <Wrapper>
              <Banner
                title="Sent!"
                description="A new link has been sent."
                icon={<ApprovedIcon fill={themeVar('context.light')} />}
                bgColor={themeVar('context.success')}
              />
              <IconWrapper>
                <svg xmlns="http://www.w3.org/2000/svg" width="256" height="132" viewBox="0 0 256 132" fill="none">
                  <g clipPath="url(#clip0_13165_29432)">
                    <path
                      d="M128.744 107.687C108.851 107.687 88.9807 107.687 69.0877 107.687C63.1952 107.687 58.7872 104.924 56.8002 100.036C55.8409 97.7063 55.6582 95.2626 56.1607 92.8416C61.5964 66.2796 67.0778 39.7404 72.5592 13.1784C74.0895 5.68708 80.9869 0.0229545 88.6837 0.0229545C128.835 0.000115271 168.964 -0.022724 209.115 0.0229545C217.725 0.0229545 223.207 7.05744 221.471 15.4851C216.058 41.6132 210.691 67.7641 205.324 93.8922C203.656 102.046 196.896 107.664 188.56 107.664C168.621 107.664 148.682 107.664 128.744 107.664V107.687ZM210.6 18.34C210.097 18.6826 209.937 18.7968 209.777 18.911C188.925 34.2361 168.096 49.5612 147.244 64.8864C140.278 70.0023 131.53 69.203 125.615 62.945C111.477 47.9853 97.317 33.0713 83.1566 18.1345C82.8369 17.8147 82.4943 17.5178 82.0147 17.061C81.9233 17.4493 81.8548 17.632 81.832 17.8147C80.827 22.748 79.7993 27.6813 78.7944 32.6374C74.5919 53.1242 70.3667 73.5881 66.1643 94.0749C65.639 96.6786 66.4155 97.615 69.0877 97.615C108.919 97.615 148.751 97.615 188.56 97.615C192.1 97.615 194.726 95.4224 195.434 91.9737C199.637 71.5554 203.862 51.1371 208.087 30.7189C208.909 26.722 209.709 22.7251 210.6 18.34ZM89.5516 10.1864C89.8257 10.5518 89.9171 10.6889 90.0084 10.8031C104.351 25.9226 118.694 41.0422 133.038 56.1389C135.458 58.6969 138.405 58.9025 141.305 56.7556C160.787 42.4354 180.246 28.138 199.728 13.8178C201.281 12.6759 202.811 11.5111 204.616 10.1864H89.5516Z"
                      fill="#A3A3A3"
                    />
                    <path
                      d="M29.4839 43.6688C37.2949 43.6688 45.1059 43.6688 52.9169 43.6688C55.315 43.6688 57.2335 45.1077 57.8502 47.2089C58.4669 49.3558 57.736 51.6626 55.8403 52.7817C54.9268 53.3298 53.762 53.6724 52.6885 53.6953C37.1122 53.7638 21.5358 53.7638 5.9823 53.7181C2.71629 53.7181 0.409531 51.457 0.478049 48.5564C0.546567 45.6787 2.80765 43.646 6.02798 43.646C13.839 43.646 21.65 43.646 29.461 43.646L29.4839 43.6688Z"
                      fill="#A3A3A3"
                    />
                    <path
                      d="M44.009 22.8164C39.0072 22.8164 34.0282 22.8164 29.0264 22.8164C26.5141 22.8164 24.7327 21.5374 23.979 19.4134C23.2938 17.4263 23.7963 15.2109 25.532 13.9776C26.4685 13.3153 27.7475 12.79 28.8666 12.79C38.893 12.6986 48.9423 12.7215 58.9687 12.7443C62.0291 12.7443 64.2217 14.914 64.2217 17.7689C64.2217 20.6238 62.052 22.7479 58.9687 22.7936C53.9669 22.8392 48.9879 22.7936 43.9861 22.7936L44.009 22.8164Z"
                      fill="#A3A3A3"
                    />
                    <path
                      d="M33.7776 71.4641C37.4319 71.4641 41.109 71.4641 44.7633 71.4641C48.0978 71.4641 50.2904 73.4968 50.2904 76.5116C50.2904 79.5035 48.0521 81.559 44.7405 81.559C37.3634 81.559 29.9863 81.559 22.6092 81.559C19.4346 81.559 17.1507 79.4578 17.105 76.5801C17.0365 73.6567 19.3661 71.487 22.6092 71.4641C26.332 71.4641 30.032 71.4641 33.7548 71.4641H33.7776Z"
                      fill="#A3A3A3"
                    />
                    <path
                      d="M215.006 131.029C237.37 131.029 255.5 112.899 255.5 90.5347C255.5 68.1705 237.37 50.0408 215.006 50.0408C192.641 50.0408 174.512 68.1705 174.512 90.5347C174.512 112.899 192.641 131.029 215.006 131.029Z"
                      fill="white"
                    />
                    <path
                      d="M241.522 84.3225C242.002 86.3323 242.23 88.365 242.23 90.4206C242.23 96.4501 240.243 102.343 236.543 107.116C232.844 111.889 227.682 115.315 221.835 116.823C215.988 118.33 209.799 117.85 204.249 115.475C198.699 113.077 194.108 108.92 191.185 103.622C188.261 98.3229 187.188 92.2249 188.147 86.2638C189.084 80.3028 191.984 74.8214 196.392 70.6875C200.8 66.5536 206.441 63.9956 212.448 63.4246C218.455 62.8536 224.484 64.2925 229.6 67.5357L234.488 62.6481C228.801 58.6512 222.018 56.5271 215.075 56.55C207.835 56.55 200.8 58.8339 194.953 63.082C189.106 67.3529 184.79 73.3597 182.597 80.2571C180.405 87.1546 180.473 94.5773 182.78 101.429C185.087 108.281 189.54 114.219 195.456 118.376C201.371 122.533 208.474 124.702 215.691 124.542C222.931 124.382 229.92 121.939 235.676 117.554C241.431 113.146 245.611 107.048 247.643 100.104C249.676 93.1613 249.448 85.7614 246.958 78.9553L241.5 84.3453L241.522 84.3225Z"
                      fill="#E5E5E5"
                    />
                    <path
                      d="M249.014 67.2158L210.324 105.905L195.936 91.5167L200.732 86.7205L210.347 96.3358L244.263 62.3967L249.037 67.2158H249.014Z"
                      fill="#434343"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_13165_29432">
                      <rect width="255" height="131.029" fill="white" transform="translate(0.5)" />
                    </clipPath>
                  </defs>
                </svg>
              </IconWrapper>
              <HelperText>We’ve emailed you a new link to sign the documents.</HelperText>
            </Wrapper>
          </>
        );
      case STATUS_ENUM.EXPIRED:
        return (
          <>
            <SubTitle>Manager Signature</SubTitle>
            <Wrapper>
              <Banner
                title="Expired Link"
                description="The link for Manager Signature has expired. Please visit the transactions page to sign the documents."
                icon={<RejectedBannerIcon fill={themeVar('context.light')} />}
                bgColor={themeVar('context.error')}
              />
              <IconWrapper>
                <ExpiredIcon />
              </IconWrapper>
              <HelperText>Please visit the transactions page to sign the documents.</HelperText>
              <RouteToTransactionsPageButtonWrapper>
                <Button width="max-content" size="lg" onClick={routeToTransactionsPage} styleType={'default'} isLoading={false}>
                  Transactions Page
                </Button>
              </RouteToTransactionsPageButtonWrapper>
            </Wrapper>
          </>
        );
    }
  };

  return (
    <>
      <Head>
        <title>Manager Signature</title>
      </Head>
      <MainWrap>
        <ManagerSignatureExpiredHeader>
          <img src={process.env.REACT_APP_NAVBAR_LOGO} width={168} height={55} />
        </ManagerSignatureExpiredHeader>
        <PaddingWrap>
          <ManagerSignatureExpiredWrapper isMobile={isMobile}>{getContentByStatus()}</ManagerSignatureExpiredWrapper>
        </PaddingWrap>
      </MainWrap>
    </>
  );
};

export default ManagerSignatureExpiredPage;

const ManagerSignatureExpiredHeader = styled.header`
  height: 108px;
  background-color: ${themeVar('menu.tab')};
  display: flex;
  align-items: center;
  padding: 0 50px;
`;

const ManagerSignatureExpiredWrapper = styled.div<{ isMobile: boolean }>`
  margin: 50px 0;
  padding: ${({ isMobile }) => (isMobile ? '20px' : '40px')};
  background: ${themeVar('layer.1')};
  border: 1px solid ${themeVar('border.base')};
  border-radius: 10px;
  height: calc(100vh - 250px);
  overflow-y: auto;
`;

const SubTitle = styled.p`
  font-size: 20px;
  font-weight: 700;
  line-height: 22.4px;
  color: ${themeVar('font.base')};
`;

const Wrapper = styled.div`
  margin-top: 20px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const IconWrapper = styled.div`
  padding: 80px 0 40px 0;
  display: flex;
  justify-content: center;
`;

const HelperText = styled.p`
  text-align: center;
  font-size: 19px;
  font-weight: 400;
  line-height: 23px;
  color: ${themeVar('font.base')};
`;

const RouteToTransactionsPageButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
`;
